import React, { useEffect, useState } from 'react';
import { Divider, Stack, Typography } from '@mui/material';

import { AddButton } from 'components/Button';
import { Search } from 'components/Search';
import { YellowMessageBar } from 'components/YellowMessageBar';

import { useDeleteLevel } from '../api/deleteLevel';
import { useReorderLevels } from '../api/reorderLevels';
import { useDragOrder } from '../hooks/useDragOrder';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { LevelFormModal } from './LevelFormModal';
import { LevelListItem } from './LevelListItem';

export const LevelsView = ({ resource, id, structure, onChanged, onModalChange }) => {
  const [editedLevel, setEditedLevel] = useState(null);
  const [deletedLevel, setDeletedLevel] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    onModalChange(!!editedLevel || !!deletedLevel);
  }, [editedLevel, deletedLevel, onModalChange]);

  const structureId = structure.level_structure_id;
  const { mutateAsync: deleteLevel } = useDeleteLevel(resource, id, () => {
    onChanged();
    setDeletedLevel(null);
  });
  const { mutateAsync: reorderLevels } = useReorderLevels(resource, id, onChanged);
  const { order, draggedId, dragHandlers } = useDragOrder(
    structure.levels,
    'level_id',
    (levelIds) => reorderLevels({ structureId, levelIds })
  );

  const searchTerm = search.trim().toLowerCase();
  const filteredLevels = order.filter(
    ({ name, description }) =>
      name.toLowerCase().includes(searchTerm) || description.toLowerCase().includes(searchTerm)
  );
  const canDrag = !searchTerm && order.length > 1;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <Search
          handleSearchChange={({ target: { value } }) => setSearch(value)}
          sx={{ minWidth: 200, width: 270 }}
        />
        <AddButton
          onClick={() => setEditedLevel({})}
          sx={({ palette }) => ({
            svg: { mt: -0.5 },
            minWidth: 200,
            border: `1px solid ${palette.grey[200]}`,
          })}
          color="secondary"
        >
          Add level
        </AddButton>
      </Stack>

      <Divider sx={{ my: 2 }} />

      <YellowMessageBar
        message="Order the levels with the highest level on top. Drag a level to move it to the right place."
        sx={{ height: 40, mb: 2, mt: 0 }}
      />

      {filteredLevels.length === 0 && (
        <Stack flex={1} alignItems="center" justifyContent="center" gap={1}>
          <Typography variant="h5" fontWeight={700}>
            No levels found
          </Typography>
          <Typography color="text.secondary">Please try another search term</Typography>
        </Stack>
      )}

      <Stack gap={1}>
        {filteredLevels.map((level) => (
          <LevelListItem
            key={level.level_id}
            canDrag={canDrag}
            isDragged={draggedId === level.level_id}
            dragHandlers={dragHandlers(level.level_id)}
            markForEdit={() => setEditedLevel(level)}
            markForDelete={
              order.length > 1 && !structure.role_count ? () => setDeletedLevel(level) : undefined
            }
            {...level}
          />
        ))}
      </Stack>

      <LevelFormModal
        resource={resource}
        id={id}
        structureId={structureId}
        level={editedLevel}
        open={!!editedLevel}
        handleClose={() => setEditedLevel(null)}
        onSaved={onChanged}
      />

      <DeleteConfirmModal
        title="Delete level"
        label="level"
        name={deletedLevel?.name}
        open={!!deletedLevel}
        handleClose={() => setDeletedLevel(null)}
        onConfirm={() =>
          deleteLevel({ structureId, levelId: deletedLevel.level_id }).catch(() => {})
        }
      />
    </>
  );
};
